/**
 * RBAC 權限管理：權限目錄、角色 CRUD、使用者角色與帳號狀態。
 *
 * 型別全部取自 generated schema，勿再手抄。
 */

import { apiFetch, buildQueryString, type Schemas } from './core';

/** 單一權限 — 對齊 BE PermissionItem。 */
export type RbacPermission = Schemas['PermissionItemDto'];

/** 權限目錄之 module 分組。 */
export type RbacPermissionModule = Schemas['PermissionModuleGroupDto'];

export type RbacPermissionCatalog = Schemas['PermissionCatalogResponseDto'];

/** 角色列表項 — 對齊 BE RoleListItem。 */
export type RbacRoleListItem = Schemas['RoleListItemDto'];

export type RbacRoleListResponse = Schemas['RoleListResponseDto'];

/** 角色詳情 — 對齊 BE RoleDetail。 */
export type RbacRoleDetail = Schemas['RoleDetailResponseDto'];

/** 使用者列表項 — 對齊 BE UserListItem。 */
export type RbacUserListItem = Schemas['UserListItemDto'];

export type RbacUserListResponse = Schemas['UserListResponseDto'];

export interface ListUsersQuery {
  q?: string;
  page?: number;
  limit?: number;
}

/** GET /admin/permissions — 權限目錄（依 module 分組，唯讀）。 */
export function getPermissionCatalog(): Promise<RbacPermissionCatalog> {
  return apiFetch<RbacPermissionCatalog>('/admin/permissions', { method: 'GET' });
}

/** GET /admin/roles — 角色列表 + 權限數 / 使用者數。 */
export function listRoles(): Promise<RbacRoleListResponse> {
  return apiFetch<RbacRoleListResponse>('/admin/roles', { method: 'GET' });
}

/** GET /admin/roles/:id — 角色詳情 + 權限清單。 */
export function getRole(id: string): Promise<RbacRoleDetail> {
  return apiFetch<RbacRoleDetail>(`/admin/roles/${encodeURIComponent(id)}`, { method: 'GET' });
}

/** GET /admin/users — 使用者列表（分頁 + email 搜尋）+ 角色。 */
export function listUsers(query: ListUsersQuery = {}): Promise<RbacUserListResponse> {
  const qs = buildQueryString({ q: query.q, page: query.page, limit: query.limit });
  return apiFetch<RbacUserListResponse>(`/admin/users${qs}`, { method: 'GET' });
}

/**
 * PUT /admin/roles/:id/permissions — 設定角色之權限集（覆蓋式，切片 2）。
 * 需 admin.rbac_manage 權限；回更新後之 RoleDetail。
 */
export function setRolePermissions(
  roleId: string,
  permissionIds: string[],
): Promise<RbacRoleDetail> {
  return apiFetch<RbacRoleDetail>(`/admin/roles/${encodeURIComponent(roleId)}/permissions`, {
    method: 'PUT',
    body: { permission_ids: permissionIds },
  });
}

/** 使用者詳情頁之角色參照（附 require_2fa）。 */
export type RbacUserRoleRef = Schemas['UserRoleRefDto'];

/** 使用者詳情 — 對齊 BE UserDetail。 */
export type RbacUserDetail = Schemas['UserDetailResponseDto'];

/** GET /admin/users/:id — 使用者詳情 + 所掛角色。 */
export function getUser(id: string): Promise<RbacUserDetail> {
  return apiFetch<RbacUserDetail>(`/admin/users/${encodeURIComponent(id)}`, { method: 'GET' });
}

/**
 * PUT /admin/users/:id/roles — 設定使用者之角色集（覆蓋式，切片 3）。
 * 需 admin.rbac_manage 權限；回更新後之 UserDetail。
 */
export function setUserRoles(userId: string, roleIds: string[]): Promise<RbacUserDetail> {
  return apiFetch<RbacUserDetail>(`/admin/users/${encodeURIComponent(userId)}/roles`, {
    method: 'PUT',
    body: { role_ids: roleIds },
  });
}

// ---------- RBAC 切片 4：角色 CRUD + 帳號狀態 ----------

export interface CreateRoleInput {
  key: string;
  display_name: string;
  description?: string;
  require_2fa?: boolean;
}

export interface UpdateRoleInput {
  display_name?: string;
  description?: string;
  require_2fa?: boolean;
}

export type AccountStatus = 'active' | 'suspended' | 'deactivated';

export interface SetUserStatusInput {
  account_status?: AccountStatus;
  unlock?: boolean;
}

/** POST /admin/roles — 新增自訂角色（需 admin.rbac_manage）。 */
export function createRole(input: CreateRoleInput): Promise<RbacRoleDetail> {
  return apiFetch<RbacRoleDetail>('/admin/roles', { method: 'POST', body: input });
}

/** PATCH /admin/roles/:id — 編輯角色 meta（需 admin.rbac_manage）。 */
export function updateRole(id: string, patch: UpdateRoleInput): Promise<RbacRoleDetail> {
  return apiFetch<RbacRoleDetail>(`/admin/roles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
  });
}

/** DELETE /admin/roles/:id — 刪除自訂角色（需 admin.rbac_manage；系統角色/使用中 → 409）。 */
export function deleteRole(id: string): Promise<void> {
  return apiFetch<void>(`/admin/roles/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** PATCH /admin/users/:id/status — 設定帳號狀態 / 解鎖（需 admin.rbac_manage）。 */
export function setUserStatus(userId: string, input: SetUserStatusInput): Promise<RbacUserDetail> {
  return apiFetch<RbacUserDetail>(`/admin/users/${encodeURIComponent(userId)}/status`, {
    method: 'PATCH',
    body: input,
  });
}
